import { SearchBar, Chip, type Column } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { ToastType } from "@legal-500/legal500-design-system";
import { DataTable } from "../../components/DataTable/DataTable";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { Toggle } from "../../components/Toggle/Toggle";
import {
  useGatewayRoutes,
  useDeleteGatewayRoute,
  useSetGatewayRouteEnabled,
} from "../../hooks/useGatewayRoutes";
import { usePermissions } from "../../hooks/usePermissions";
import type { GatewayRouteEntry } from "../../api/gatewayRoutes.service";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import styles from "./GatewayAllowlist.module.css";
import AddRouteModal from "./AddRouteModal";

type ShowFilter = "all" | "enabled";

type OutletContext = {
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
  onMutationSuccess?: () => void;
};

function methodBadgeClass(method: string): string {
  switch (method.toUpperCase()) {
    case "GET":
      return styles.methodGet;
    case "POST":
      return styles.methodPost;
    case "PUT":
      return styles.methodPut;
    case "PATCH":
      return styles.methodPatch;
    case "DELETE":
      return styles.methodDelete;
    default:
      return styles.methodDefault;
  }
}

function buildColumns(
  canWrite: boolean,
  onToggle: (row: GatewayRouteEntry) => void,
  onRemove: (row: GatewayRouteEntry) => void,
): Column<GatewayRouteEntry>[] {
  return [
    {
      header: "Service",
      accessor: "serviceName",
    },
    {
      header: "Method",
      accessor: "method",
      render: (_value: unknown, row: GatewayRouteEntry) => (
        <span className={`${styles.methodBadge} ${methodBadgeClass(row.method)}`}>
          {row.method.toUpperCase()}
        </span>
      ),
    },
    {
      header: "Path",
      accessor: "path",
      render: (_value: unknown, row: GatewayRouteEntry) => (
        <code className={styles.pathCode} title={row.path}>
          {row.path}
        </code>
      ),
    },
    {
      header: "Exposed",
      accessor: "enabled",
      render: (_value: unknown, row: GatewayRouteEntry) => {
        if (row.stale) {
          return (
            <div className={styles.exposedCell}>
              <Chip color="warning" size="small">
                stale
              </Chip>
              {canWrite && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => onRemove(row)}
                  aria-label={`Remove stale route ${row.method} ${row.path}`}
                >
                  ✕
                </button>
              )}
            </div>
          );
        }
        return (
          <Toggle
            checked={row.enabled}
            disabled={!canWrite}
            ariaLabel={`${row.enabled ? "Disable" : "Enable"} ${row.method} ${row.path}`}
            onChange={() => onToggle(row)}
          />
        );
      },
    },
    {
      header: "Added By",
      accessor: "createdBy",
      render: (_value: unknown, row: GatewayRouteEntry) => {
        const addedBy = row.createdBy ?? "—";
        return (
          <span className={styles.addedByCell} title={addedBy === "—" ? undefined : addedBy}>
            {addedBy}
          </span>
        );
      },
    },
    {
      header: "When",
      accessor: "createdAt",
      render: (_value: unknown, row: GatewayRouteEntry) => (
        <span className={styles.whenCell}>
          {row.createdAt ? formatDateTimeShort(row.createdAt) : "—"}
        </span>
      ),
    },
  ];
}

function matchesSearch(row: GatewayRouteEntry, term: string): boolean {
  const lower = term.toLowerCase();
  return row.serviceName.toLowerCase().includes(lower) || row.path.toLowerCase().includes(lower);
}

function GatewayAllowlistList() {
  const context = useOutletContext<OutletContext>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilter, setShowFilter] = useState<ShowFilter>("all");
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("platform.write");

  const {
    data: routesData,
    loading: routesLoading,
    error: routesError,
    refetch: refetchRoutes,
  } = useGatewayRoutes();

  const setEnabled = useSetGatewayRouteEnabled();
  const deleteRoute = useDeleteGatewayRoute();

  async function handleToggle(row: GatewayRouteEntry) {
    try {
      await setEnabled.mutate({ id: row.id, enabled: !row.enabled });
      await refetchRoutes();
      context.onMutationSuccess?.();
    } catch {
      context.setToast?.({
        message: `Failed to ${row.enabled ? "disable" : "enable"} route`,
        type: "error",
      });
    }
  }

  async function handleRemove(row: GatewayRouteEntry) {
    try {
      await deleteRoute.mutate(row.id);
      await refetchRoutes();
      context.onMutationSuccess?.();
      context.setToast?.({ message: "Route removed", type: "success" });
    } catch {
      context.setToast?.({ message: "Failed to remove route", type: "error" });
    }
  }

  function handleAddSuccess() {
    setAddModalOpen(false);
    refetchRoutes();
    context.onMutationSuccess?.();
    context.setToast?.({ message: "Route added", type: "success" });
  }

  const columns = buildColumns(canWrite, handleToggle, handleRemove);

  if (routesError) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Could not load gateway allowlist: ${routesError.message}`}
      />
    );
  }

  const allRows = [...(routesData?.routes ?? [])].sort((a, b) =>
    `${a.serviceName}${a.path}${a.method}`.localeCompare(`${b.serviceName}${b.path}${b.method}`),
  );

  const filteredRows = allRows
    .filter(row => showFilter === "all" || row.enabled)
    .filter(row => searchTerm === "" || matchesSearch(row, searchTerm));

  const isEmpty = !routesLoading && allRows.length === 0;

  return (
    <div>
      <div className={styles.controlsBar}>
        <div className={styles.searchWrapper}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by service or path..."
          />
        </div>
        <div className={styles.filterWrapper}>
          <label htmlFor="show-filter" className={styles.filterLabel}>
            Show
          </label>
          <select
            id="show-filter"
            value={showFilter}
            onChange={e => setShowFilter(e.target.value as ShowFilter)}
            className={styles.filterSelect}
          >
            <option value="all">All</option>
            <option value="enabled">Enabled only</option>
          </select>
        </div>
        {canWrite && (
          <button
            type="button"
            className={styles.addRouteButton}
            onClick={() => setAddModalOpen(true)}
          >
            Add route
          </button>
        )}
      </div>

      <div className={styles.contentSection}>
        {routesLoading ? (
          <LoadingState />
        ) : isEmpty ? (
          <EmptyState
            variant="empty"
            title="No routes in allowlist"
            message="No routes have been added yet. Use 'Add route' to expose service routes."
          />
        ) : (
          <DataTable
            data={filteredRows}
            columns={columns}
            emptyMessage="No routes match your filter."
            rowKey="id"
          />
        )}
      </div>

      {addModalOpen && (
        <AddRouteModal
          existingRoutes={allRows}
          onSuccess={handleAddSuccess}
          onClose={() => setAddModalOpen(false)}
        />
      )}
    </div>
  );
}

export default GatewayAllowlistList;
